import styled from 'styled-components';
import { Text12Regular, Text14Bold, Text14Regular, Text17Bold } from '../../utils/Text/Text.styles';

export const CommentsSheet = styled.div`
  width: 100%;
  // Size against the locked full height (--app-height), not the shrinking
  // viewport, so the sheet's top stays put when the keyboard opens. The Telegram
  // top inset keeps it clear of the native Close / ⌄ / ⋯ controls.
  height: min(56rem, calc(var(--app-height, 100dvh) - var(--tg-top-inset, 0px) - 1.2rem));
  max-height: calc(var(--app-height, 100dvh) - var(--tg-top-inset, 0px) - 1.2rem);
  // Lift the composer above the keyboard by shrinking the content area from the
  // bottom; the header / comment list above it do not move. When the keyboard is
  // closed (lift 0) this falls back to the home-indicator safe area, so the
  // composer never jumps as that inset appears / disappears. The short
  // transition lets it slide with the keyboard instead of snapping.
  padding-bottom: max(var(--keyboard-lift, 0px), env(safe-area-inset-bottom, 0px));
  // ~iOS keyboard duration. Long enough that the exact-height correction lands
  // while the slide is still moving, so it reads as one continuous motion.
  transition: padding-bottom 0.32s cubic-bezier(0.22, 0.61, 0.36, 1);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  background: #111214;
  border-radius: 2.8rem 2.8rem 0 0;
  overflow: hidden;

  @media (min-width: 761px) {
    height: min(72rem, calc(100dvh - 4.8rem));
    max-height: calc(100dvh - 4.8rem);
    border-radius: 2.8rem;
  }
`;

export const CommentsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.8rem 1.8rem 1rem;

  @media (min-width: 761px) {
    padding: 2.4rem 2.4rem 1.2rem;
  }
`;

export const CommentsCountText = styled(Text17Bold)`
  color: #fff;
`;

export const CommentsSubtitle = styled(Text12Regular)`
  color: rgba(255, 255, 255, 0.45);
`;

export const CloseButton = styled.button`
  width: 3.8rem;
  height: 3.8rem;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 1.4rem;
    height: 1.4rem;
    color: #fff;
  }
`;

export const CommentsBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 1.8rem 1.2rem;
  overscroll-behavior: contain;

  @media (min-width: 761px) {
    padding: 0 2.4rem 1.6rem;
  }
`;

export const CommentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
`;

export const CommentCard = styled.div`
  display: grid;
  grid-template-columns: 4.2rem 1fr;
  gap: 1.2rem;
`;

const AvatarBase = `
  width: 4.2rem;
  height: 4.2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  object-fit: cover;
`;

export const CommentAvatar = styled.img`
  ${AvatarBase}
`;

export const AvatarFallback = styled.div`
  ${AvatarBase}
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  font-size: 1.8rem;
  font-weight: 600;
`;

export const CommentBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const CommentThread = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const CommentHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
`;

export const CommentMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

export const CommentName = styled(Text14Bold)`
  color: #fff;
  word-break: break-all;
`;

export const CommentText = styled(Text14Regular)`
  color: rgba(255, 255, 255, 0.88);
  line-height: 1.45;
  word-break: break-all;
`;

export const CommentReplyText = styled(Text12Regular)`
  color: rgba(255, 255, 255, 0.42);
`;

export const CommentActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  flex-wrap: wrap;
`;

export const CommentActionButton = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  color: rgba(255, 255, 255, 0.52);
  cursor: pointer;
  font-size: 1.2rem;
  font-weight: 600;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const CommentDeleteButton = styled(CommentActionButton)`
  color: rgba(255, 107, 129, 0.92);
`;

export const CommentLikeButton = styled.button`
  border: none;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
  color: ${({ $active }) => ($active ? '#ff375f' : 'rgba(255, 255, 255, 0.52)')};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};

  svg {
    width: 1.8rem;
    height: 1.8rem;
    color: inherit;
  }

  span {
    font-size: 1.1rem;
    font-weight: 600;
    color: inherit;
  }
`;

export const CommentsEmptyState = styled(Text14Regular)`
  min-height: 18rem;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: rgba(255, 255, 255, 0.62);
`;

export const LoadMoreButton = styled.button`
  width: 100%;
  min-height: 4.2rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 1.6rem;
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.88);
  font-size: 1.3rem;
  font-weight: 600;
  padding: 1rem 1.4rem;

  &:disabled {
    opacity: 0.6;
  }
`;

export const InlineRepliesButton = styled.button`
  align-self: flex-start;
  min-height: 3.2rem;
  border: 1px solid transparent;
  border-radius: 999px;
  background: transparent;
  color: rgba(255, 255, 255, 0.78);
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.7rem 1rem 0.7rem 0.8rem;
  font-size: 1.2rem;
  font-weight: 600;
  line-height: 1;

  svg {
    width: 1.2rem;
    height: 1.2rem;
    color: currentColor;
    flex-shrink: 0;
  }

  &:disabled {
    opacity: 0.6;
  }
`;

export const RepliesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-left: 1.8rem;
  padding-left: 1.6rem;
  border-left: 1px solid rgba(255, 255, 255, 0.08);
`;

export const CommentComposer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  // Safe-area / keyboard spacing is handled by the sheet's padding-bottom so the
  // composer doesn't shift as the home-indicator inset appears / disappears.
  padding: 1.2rem 1.8rem 1.6rem;
  background: #111214;
  border-top: 1px solid rgba(255, 255, 255, 0.08);

  @media (min-width: 761px) {
    padding: 1.6rem 2.4rem 2.4rem;
  }
`;

export const ComposerInputRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1rem;
`;

export const ComposerMetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

export const ComposerMetaText = styled(Text12Regular)`
  color: rgba(255, 255, 255, 0.58);
`;

export const CommentComposerInput = styled.input`
  height: 4.8rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  padding: 0 1.6rem;
  font-size: 1.6rem;
  outline: none;

  &::placeholder {
    color: rgba(255, 255, 255, 0.46);
  }
`;

export const CommentComposerButton = styled.button`
  min-width: 7.2rem;
  height: 4.8rem;
  border: none;
  border-radius: 999px;
  background: ${({ disabled }) => (disabled ? 'rgba(255, 255, 255, 0.12)' : '#2693ff')};
  color: #fff;
  font-size: 1.4rem;
  font-weight: 600;
  padding: 0 1.6rem;
`;
